import React, { useState, useEffect } from 'react';
import { Send, Loader2, CheckCircle2, MessageSquare, Phone } from 'lucide-react';
import { auth } from '../config/firebase';
import { lembreteService } from '../services/api';

const MENSAGENS_PREDEFINIDAS = [
  { titulo: "Confirmação de horário", texto: "Oi, {nome}! Passando pra confirmar seu atendimento de {servico} hoje às {hora}. Tudo certo?" },
  { titulo: "Chegando a hora", texto: "Oi {nome}! Seu atendimento de {servico} está chegando. Te espero às {hora}!" },
  { titulo: "Aviso importante", texto: "Olá, {nome}! Passando rapidinho pra falar sobre o seu atendimento de {servico}. Qualquer dúvida, me chama por aqui." },
];

// Formulário enxuto pra disparar UMA mensagem AGORA pro cliente de um agendamento específico —
// sem os campos de data/hora de disparo do CriarLembreteAvulso, que são pra agendar o envio
// pra depois. Aqui é: escolhe um modelo (ou digita), confere o preview e aperta enviar.
const EnviarLembreteRapido = ({ consulta, onEnviado }) => {
  const [templatesSalvos, setTemplatesSalvos] = useState([]);
  const [mensagem, setMensagem] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState(null);

  const primeiroNome = (consulta.paciente_nome || 'Cliente').split(' ')[0];
  // A observação costuma trazer o nome do serviço (agendado pelo bot ou digitado na criação
  // manual) — mesma fonte já usada no worker de lembretes automáticos (lembrete.service.js).
  const servico = (consulta.observacao || '').replace(/^(Bot AtendeFlow|Bot Agenda Sincro|Agenda Sincro):\s*/i, '').trim() || 'seu atendimento';
  const dataFormatada = new Date(consulta.data_inicio).toLocaleDateString('pt-BR');
  const horaFormatada = new Date(consulta.data_inicio).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  useEffect(() => {
    const carregar = async () => {
      try {
        const token = await auth.currentUser.getIdToken();
        const res = await lembreteService.listarTemplates(token);
        setTemplatesSalvos(res.data || []);
      } catch (error) {
        console.error('Erro ao carregar modelos:', error);
      }
    };
    carregar();
  }, []);

  const resolverTags = (texto) => texto
    .replace(/{nome}/gi, primeiroNome)
    .replace(/{servico}/gi, servico)
    .replace(/{data}/gi, dataFormatada)
    .replace(/{hora}/gi, horaFormatada);

  const handleSelecionarModelo = (e) => {
    if (e.target.value) setMensagem(resolverTags(e.target.value));
  };

  const handleEnviar = async () => {
    if (!mensagem.trim()) return alert('Escreva ou selecione uma mensagem antes de enviar.');
    setErro(null);
    setEnviando(true);
    try {
      const token = await auth.currentUser.getIdToken();
      await lembreteService.enviarAgora({ telefone: consulta.paciente_telefone, mensagem }, token);
      setEnviado(true);
      setTimeout(() => {
        onEnviado?.();
      }, 1200);
    } catch (error) {
      setErro(error.response?.data?.message || 'Erro ao enviar o lembrete. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 text-left">
      <div className="flex items-center gap-3 bg-[#F8F7FF] border border-purple-50 rounded-xl px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="m-0 text-sm font-black text-slate-800 truncate">{consulta.paciente_nome}</p>
          <p className="m-0 text-xs text-slate-400 font-semibold flex items-center gap-1.5"><Phone size={10} /> {consulta.paciente_telefone}</p>
        </div>
      </div>

      {erro && (
        <div className="bg-red-50 border border-red-100 text-red-600 text-xs font-bold px-4 py-3 rounded-xl">{erro}</div>
      )}

      <div className="flex flex-col gap-1.5">
        <label className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-1.5"><MessageSquare size={12} /> Escolher modelo (opcional)</label>
        <select className="w-full bg-[#F8F7FF] border border-purple-50 py-3 px-4 rounded-xl text-sm font-semibold text-slate-700 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10 cursor-pointer" onChange={handleSelecionarModelo} defaultValue="">
          <option value="">-- Selecione ou digite abaixo --</option>
          {templatesSalvos.length > 0 && (
            <optgroup label="Meus modelos salvos">
              {templatesSalvos.map((tpl) => <option key={tpl.id} value={tpl.mensagem}>{tpl.titulo}</option>)}
            </optgroup>
          )}
          <optgroup label="Sugestões">
            {MENSAGENS_PREDEFINIDAS.map((msg, idx) => <option key={idx} value={msg.texto}>{msg.titulo}</option>)}
          </optgroup>
        </select>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-1.5"><MessageSquare size={12} /> Mensagem</label>
        <textarea
          className="w-full bg-[#F8F7FF] border border-purple-50 py-3 px-4 rounded-xl text-sm font-semibold text-slate-900 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10 min-h-[110px] resize-y font-sans"
          placeholder="Escreva a mensagem que o cliente vai receber agora..."
          value={mensagem}
          onChange={(e) => setMensagem(e.target.value)}
        />
      </div>

      <button
        onClick={handleEnviar}
        disabled={enviando || enviado || !mensagem.trim()}
        className={`w-full h-13 border-none rounded-2xl text-white text-sm font-black uppercase tracking-wider flex items-center justify-center gap-2.5 cursor-pointer transition-all duration-300 active:scale-98 disabled:opacity-50 disabled:cursor-not-allowed py-3.5 ${enviado ? 'bg-emerald-500' : 'bg-gradient-to-r from-[#1883FF] to-[#6D28D9] hover:opacity-90'}`}
      >
        {enviando ? <Loader2 size={16} className="animate-spin" /> : enviado ? <CheckCircle2 size={16} /> : <Send size={16} />}
        {enviando ? 'Enviando...' : enviado ? 'Enviado!' : 'Enviar Agora'}
      </button>
    </div>
  );
};

export default EnviarLembreteRapido;
